import React, { ElementType } from 'react';
import { createPortal } from 'react-dom';

import { ModalBody, ModalFooter, ModalTitle } from './ModalContent';
import { ModalContextProvider } from './ModalContext';
import ModalHeader from './ModalHeader';
import {
    MODAL_OVERLAY_CENTERED,
    mergeDialogClassName,
    stripLegacyModalPositionClasses,
} from './modal-layout';

interface ModalProps {
    show?: boolean;
    onHide?: () => void;
    className?: string;
    children: React.ReactNode;
    as?: ElementType;
    id?: string;
    placement?: string;
    dialogClassName?: string;
    backdropClassName?: string;
    /** Viewport-centered overlay (default true). Set false for drawer-style modals. */
    centered?: boolean;
}

const Modal = ({
    show,
    onHide,
    children,
    className,
    placement,
    id,
    dialogClassName,
    backdropClassName,
    centered = true,
    as: Component = `div`,
    ...props
}: ModalProps) => {
    const defaultBackdropClass = `backdrop-overlay fixed inset-0 z-[1049] bg-slate-900/40 dark:bg-zink-800/70`;
    const backdropClass = (backdropClassName || defaultBackdropClass).replace(/z-\[\d+\]/g, 'z-[1049]');

    const extraOverlay = stripLegacyModalPositionClasses(className);
    const overlayClass = centered
        ? [MODAL_OVERLAY_CENTERED, extraOverlay].filter(Boolean).join(' ')
        : [className || ``, !show ? `show hidden` : ``].filter(Boolean).join(' ');

    const shellClass = centered ? mergeDialogClassName(dialogClassName) : dialogClassName || ``;

    if (!show) {
        return null;
    }

    const modalNode = (
        <React.Fragment>
            <div onClick={onHide} className={backdropClass} id="backDropDiv" aria-hidden="true" />
            <div
                {...props}
                id={id ? id : `defaultModal`}
                className={overlayClass}
                role="dialog"
                aria-modal="true"
            >
                <ModalContextProvider show={show} onHide={onHide}>
                    <Component className={shellClass}>{children}</Component>
                </ModalContextProvider>
            </div>
        </React.Fragment>
    );

    return createPortal(modalNode, document.body);
};

export default Object.assign(Modal, {
    Header: ModalHeader,
    Title: ModalTitle,
    Body: ModalBody,
    Footer: ModalFooter,
});
